import {
  filterMatchingTestBlocks,
  getStudentTestBlockName,
  getStudentTestTopicName,
} from "./studentTestBlocks";
import {
  getContenidoNodesForTab,
  listPublishedCourseTabs,
  listOrderedCourseTabs,
  normalizeCourseTabName,
} from "./normalizeCourseShape";
import {
  filterVisibleTemarioNodes,
  temarioNodeHasDocumentFiles,
  temarioNodeHasVisibleContent,
} from "./campusVisibleContent";

/**
 * @param {Record<string, import("./temarioTree").TemarioNodo[]>} contenido
 * @param {string} tabLabel
 * @param {import("./temarioTree").TemarioNodo[]} nodes
 */
function assignContenidoTab(contenido, tabLabel, nodes) {
  const wanted = normalizeCourseTabName(tabLabel);
  if (!wanted) {
    return;
  }
  Object.keys(contenido).forEach((key) => {
    if (key !== wanted && normalizeCourseTabName(key) === wanted) {
      delete contenido[key];
    }
  });
  contenido[wanted] = nodes;
}

function mapTestBlocksToContenido(blocks) {
  if (!Array.isArray(blocks)) {
    return [];
  }
  return blocks.map((block, blockIndex) => {
    const blockName = getStudentTestBlockName(block, blockIndex);
    const temas = Array.isArray(block?.temas) ? block.temas : [];
    return {
      id: `test-block-${blockName}`,
      nombre: blockName,
      archivos: [],
      testRefs: [],
      hijos: temas.map((tema, topicIndex) => {
        const topicName = getStudentTestTopicName(tema, topicIndex);
        return {
          id: `test-topic-${blockName}-${topicName}`,
          nombre: topicName,
          archivos: [],
          testRefs: [{ titulo: topicName, bloque: blockName, tema: topicName }],
          hijos: [],
        };
      }),
    };
  });
}

export function buildCampusContentFromTestStructure(testStructure, tabLabel, allowFallbackToAll = true) {
  const normalizedTab = typeof tabLabel === "string" ? tabLabel.trim() : "";
  if (!normalizedTab || !Array.isArray(testStructure)) {
    return [];
  }

  const blocks = filterMatchingTestBlocks(testStructure, normalizedTab, allowFallbackToAll);
  return mapTestBlocksToContenido(blocks);
}

/**
 * Fills empty temario shells with the matching test block so later documentation
 * and previously created tests stay visible under the same course tab.
 * @param {import("./temarioTree").TemarioNodo[]} nodes
 * @param {Array<{ nombre?: string, temas?: Array<{ nombre?: string }> }>} testStructure
 * @returns {import("./temarioTree").TemarioNodo[]}
 */
export function enrichTemarioNodesWithMatchingTests(nodes, testStructure) {
  if (!Array.isArray(nodes)) {
    return [];
  }
  return nodes.map((node) => {
    if (!node || typeof node !== "object" || temarioNodeHasVisibleContent(node)) {
      return node;
    }
    const name = typeof node.nombre === "string" ? node.nombre.trim() : "";
    if (!name) {
      return node;
    }
    const fromTests = filterVisibleTemarioNodes(
      buildCampusContentFromTestStructure(testStructure, name, false),
    );
    if (fromTests.length === 0) {
      return node;
    }
    const match = fromTests[0];
    return {
      ...node,
      id: node.id || match.id,
      hijos: Array.isArray(match.hijos) ? match.hijos : [],
      testRefs: Array.isArray(match.testRefs) ? match.testRefs : node.testRefs,
    };
  });
}

/**
 * @param {import("./temarioTree").TemarioNodo[]} nodes
 * @param {Set<string>} representedNames
 * @param {Set<string>} representedTestTitles
 */
function findTestStructureTab(testStructure, title) {
  if (!Array.isArray(testStructure) || !title) {
    return "";
  }
  for (let index = 0; index < testStructure.length; index += 1) {
    const block = testStructure[index];
    const temas = Array.isArray(block?.temas) ? block.temas : [];
    const matchesTopic = temas.some((tema, topicIndex) => (
      getStudentTestTopicName(tema, topicIndex) === title
    ));
    if (matchesTopic) {
      return getStudentTestBlockName(block, index);
    }
  }
  return "";
}

function testRefCountsAsRepresentedOnTab(ref, tabLabel, testStructure, courseTabs) {
  const title = typeof ref?.titulo === "string" ? ref.titulo.trim() : "";
  if (!title) {
    return false;
  }
  const structureTab = findTestStructureTab(testStructure, title);
  if (structureTab) {
    if (structureTab === tabLabel) {
      return true;
    }
    const publishedTabs = Array.isArray(courseTabs) ? courseTabs : [];
    return !publishedTabs.includes(structureTab);
  }
  const bloque = typeof ref?.bloque === "string" ? ref.bloque.trim() : "";
  if (bloque) {
    return bloque === tabLabel;
  }
  return true;
}

function collectRepresentedTestPlacement(
  nodes,
  tabLabel,
  testStructure,
  courseTabs,
  representedNames,
  representedTestTitles,
) {
  if (!Array.isArray(nodes)) {
    return;
  }
  nodes.forEach((node) => {
    if (!node || typeof node !== "object") {
      return;
    }
    const name = typeof node.nombre === "string" ? node.nombre.trim() : "";
    if (name) {
      representedNames.add(name);
    }
    const refs = Array.isArray(node.testRefs) ? node.testRefs : [];
    refs.forEach((ref) => {
      if (testRefCountsAsRepresentedOnTab(ref, tabLabel, testStructure, courseTabs)) {
        const title = typeof ref?.titulo === "string" ? ref.titulo.trim() : "";
        if (title) {
          representedTestTitles.add(`${tabLabel}\u0000${title}`);
        }
      }
    });
    collectRepresentedTestPlacement(
      Array.isArray(node.hijos) ? node.hijos : [],
      tabLabel,
      testStructure,
      courseTabs,
      representedNames,
      representedTestTitles,
    );
  });
}

/**
 * @param {Array<{ nombre?: string, temas?: Array<{ nombre?: string }> }>} testStructure
 * @param {Set<string>} representedNames
 * @param {Set<string>} representedTestTitles
 * @returns {Array<{ nombre?: string, temas?: Array<{ nombre?: string }> }>}
 */
function leftoverTestStructure(testStructure, representedNames, representedTestTitles, courseTabs) {
  const publishedTabs = Array.isArray(courseTabs) ? courseTabs : [];
  return (Array.isArray(testStructure) ? testStructure : [])
    .map((block, index) => {
      const name = getStudentTestBlockName(block, index);
      if (!name || representedNames.has(name)) {
        return null;
      }
      const temas = Array.isArray(block?.temas) ? block.temas : [];
      const leftoverTemas = temas.filter((tema, topicIndex) => {
        const topicName = getStudentTestTopicName(tema, topicIndex);
        if (!topicName) {
          return false;
        }
        if (representedTestTitles.has(`${name}\u0000${topicName}`)) {
          return false;
        }
        if (!publishedTabs.includes(name)) {
          return ![...representedTestTitles].some((key) => key.endsWith(`\u0000${topicName}`));
        }
        return true;
      });
      if (leftoverTemas.length === 0) {
        return null;
      }
      return { ...block, nombre: name, temas: leftoverTemas };
    })
    .filter((block) => block !== null);
}

export function mergeTestStructureIntoCampusCourse(course, testStructure, courseTabs) {
  if (!course) {
    return course;
  }

  const baseContenido = course.contenido && typeof course.contenido === "object"
    ? course.contenido
    : {};
  const contenido = { ...baseContenido };
  const tabs = listPublishedCourseTabs(
    listOrderedCourseTabs(
      Array.isArray(course.pestanasCurso) ? course.pestanasCurso : [],
      Array.isArray(courseTabs) ? courseTabs : [],
    ),
    contenido,
  );

  const emptyTabCount = tabs.filter((tab) => {
    const enriched = enrichTemarioNodesWithMatchingTests(
      getContenidoNodesForTab(contenido, tab),
      testStructure,
    );
    return filterVisibleTemarioNodes(enriched).length === 0;
  }).length;
  const allowFallbackToAll = emptyTabCount === 1;

  tabs.forEach((tab) => {
    const enriched = enrichTemarioNodesWithMatchingTests(
      getContenidoNodesForTab(contenido, tab),
      testStructure,
    );
    const visible = filterVisibleTemarioNodes(enriched);
    if (visible.length > 0) {
      assignContenidoTab(contenido, tab, visible);
    }
  });

  const representedNames = new Set();
  const representedTestTitles = new Set();
  tabs.forEach((tab) => {
    collectRepresentedTestPlacement(
      getContenidoNodesForTab(contenido, tab),
      tab,
      testStructure,
      tabs,
      representedNames,
      representedTestTitles,
    );
  });
  const leftoverStructure = leftoverTestStructure(
    testStructure,
    representedNames,
    representedTestTitles,
    tabs,
  );

  tabs.forEach((tab) => {
    if (filterVisibleTemarioNodes(getContenidoNodesForTab(contenido, tab)).length > 0) {
      return;
    }
    assignContenidoTab(
      contenido,
      tab,
      filterVisibleTemarioNodes(
        buildCampusContentFromTestStructure(leftoverStructure, tab, allowFallbackToAll),
      ),
    );
  });

  representedNames.clear();
  representedTestTitles.clear();
  tabs.forEach((tab) => {
    collectRepresentedTestPlacement(
      getContenidoNodesForTab(contenido, tab),
      tab,
      testStructure,
      tabs,
      representedNames,
      representedTestTitles,
    );
  });
  const unmatched = leftoverTestStructure(
    testStructure,
    representedNames,
    representedTestTitles,
    tabs,
  );
  if (unmatched.length > 0 && tabs.length > 0) {
    const candidateTabs = tabs.filter((tab) => {
      const nodes = getContenidoNodesForTab(contenido, tab);
      return !nodes.some((node) => temarioNodeHasDocumentFiles(node));
    });
    const destination = candidateTabs.length === 1
      ? candidateTabs[0]
      : (candidateTabs.length === 0 ? tabs[tabs.length - 1] : "");
    if (destination) {
      assignContenidoTab(contenido, destination, [
        ...getContenidoNodesForTab(contenido, destination),
        ...filterVisibleTemarioNodes(mapTestBlocksToContenido(unmatched)),
      ]);
    }
  }

  return { ...course, pestanasCurso: tabs, contenido };
}

export function resolveTestStructureNavPath(course, pestana, bloque, tema) {
  const tab = typeof pestana === "string" ? pestana.trim() : "";
  const blockName = typeof bloque === "string" ? bloque.trim() : "";
  const topicName = typeof tema === "string" ? tema.trim() : "";
  if (!course || !tab || !blockName) {
    return [];
  }

  const blocks = getContenidoNodesForTab(course.contenido, tab);
  const blockIndex = blocks.findIndex((block) => block?.nombre === blockName);
  if (blockIndex < 0) {
    return [];
  }
  if (!topicName) {
    return [blockIndex];
  }
  const topics = Array.isArray(blocks[blockIndex]?.hijos) ? blocks[blockIndex].hijos : [];
  const topicIndex = topics.findIndex((item) => item?.nombre === topicName);
  return topicIndex >= 0 ? [blockIndex, topicIndex] : [blockIndex];
}
